import React, { useState } from 'react';
import ZuweisungForm from '../components/zuweisung/ZuweisungForm';
import ZuweisungList from '../components/zuweisung/ZuweisungList';
import ZuweisungFilter from '../components/zuweisung/ZuweisungFilter';
import WeeklyScheduleView from '../components/zuweisung/WeeklyScheduleView';
import DeleteZuweisungDialog from '../components/zuweisung/DeleteZuweisungDialog';
import DefaultScheduleView from '../components/zuweisung/DefaultScheduleView';
import { Zuweisung, ZuweisungFilter as FilterType, AppType } from '../types';
import { zuweisungService } from '../services/zuweisung.service';
import { useEnvironment } from '../context/EnvironmentContext';

const ZuweisungPage: React.FC = () => {
  const { currentApp } = useEnvironment();
  const [showForm, setShowForm] = useState(false);
  const [viewMode, setViewMode] = useState<'week' | 'list'>('week');
  const [selectedZuweisung, setSelectedZuweisung] = useState<Zuweisung | null>(null);
  const [zuweisungToDelete, setZuweisungToDelete] = useState<Zuweisung | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterType>({});

  const handleCreateNew = () => {
    setSelectedZuweisung(null);
    setShowForm(true);
  };

  const handleEdit = (zuweisung: Zuweisung) => {
    setSelectedZuweisung(zuweisung);
    setShowForm(true);
  };

  const handleSuccess = () => {
    setShowForm(false);
    setSelectedZuweisung(null);
    setRefreshTrigger(prev => prev + 1);
  };

  const handleCancel = () => {
    setShowForm(false);
    setSelectedZuweisung(null);
  };

  const handleDeleteClick = (zuweisung: Zuweisung) => {
    setZuweisungToDelete(zuweisung);
  };

  const handleDeleteConfirm = async () => {
    if (!zuweisungToDelete) return;

    try {
      setError(null);
      await zuweisungService.deleteZuweisung(zuweisungToDelete.id);
      setZuweisungToDelete(null);
      setRefreshTrigger(prev => prev + 1);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Löschen der Zuweisung');
      setZuweisungToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setZuweisungToDelete(null);
  };

  const handleFilterChange = (newFilter: FilterType) => {
    setFilter(newFilter);
  };

  return (
    <div>
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <h1 className="text-3xl font-bold text-gray-900">Zuweisungen</h1>

          {!showForm && (
            <div className="flex gap-2">
              <button
                onClick={() => setViewMode('week')}
                className={`px-4 py-2 min-h-[44px] rounded-md transition-colors ${viewMode === 'week'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
              >
                📅 Wochenansicht
              </button>

              {currentApp === AppType.LEGACY ? (
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-4 py-2 min-h-[44px] rounded-md transition-colors ${viewMode === 'list'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                >
                  📋 Listenansicht
                </button>
              ) : (
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-4 py-2 min-h-[44px] rounded-md transition-colors ${viewMode === 'list'
                    ? 'bg-yellow-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                >
                  🗓️ Feste Zuordnung
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {showForm ? (
        <ZuweisungForm
          zuweisung={selectedZuweisung}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
        />
      ) : viewMode === 'week' ? (
        <WeeklyScheduleView refreshTrigger={refreshTrigger} />
      ) : (
        currentApp === AppType.LEGACY ? (
          <>
            <ZuweisungFilter onFilterChange={handleFilterChange} />
            <ZuweisungList
              onEdit={handleEdit}
              onDelete={handleDeleteClick}
              onCreate={handleCreateNew}
              refreshTrigger={refreshTrigger}
              filter={filter}
            />
          </>
        ) : (
          <>
            <ZuweisungFilter onFilterChange={handleFilterChange} />
            <DefaultScheduleView refreshTrigger={refreshTrigger} filter={filter} />
          </>
        )
      )}

      {zuweisungToDelete && (
        <DeleteZuweisungDialog
          zuweisung={zuweisungToDelete}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
        />
      )}
    </div>
  );
};

export default ZuweisungPage;
